import React, { useEffect, useState, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { format, parseISO } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Chip from "@material-ui/core/Chip";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Avatar from "@material-ui/core/Avatar";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { toast } from "react-toastify";

import EditIcon from "@material-ui/icons/Edit";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import MemoryOutlinedIcon from "@material-ui/icons/MemoryOutlined";

import api from "../../services/api";
import MainContainer from "../../components/MainContainer";
import ClientKeyDates from "../../components/ClientKeyDates";
import ClientCommercial from "../../components/ClientCommercial";
import PageHeader from "../../components/PageHeader";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ClientModal from "../../components/ClientModal";
import toastError from "../../errors/toastError";
import withMediaToken from "../../services/mediaUrl";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  scroll: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(1),
    ...theme.scrollbarStyles,
  },
  field: {
    marginBottom: theme.spacing(1),
  },
  label: {
    fontSize: 11,
    color: "#777",
    textTransform: "uppercase",
  },
  value: {
    fontSize: 14,
  },
  chipRow: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    marginTop: 4,
  },
  statusChip: {
    color: "#fff",
    fontWeight: 600,
  },
  addressBox: {
    border: "1px solid #eee",
    borderRadius: 6,
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(1),
    fontSize: 13,
  },
}));

const displayName = (c) => {
  if (!c) return "";
  if (c.personType === "PJ")
    return c.tradeName || c.companyName || "(Sem nome)";
  return [c.firstName, c.lastName].filter(Boolean).join(" ") || "(Sem nome)";
};

const displayDoc = (c) => (c?.personType === "PJ" ? c?.cnpj : c?.cpf);

// Subtítulo do cabeçalho: razão social (quando difere do nome fantasia
// exibido) e documento. É o que distingue dois clientes de nome parecido.
const clientSubtitle = (c) => {
  if (!c) return null;
  const partes = [];
  if (c.personType === "PJ" && c.companyName && c.companyName !== displayName(c)) {
    partes.push(c.companyName);
  }
  const doc = displayDoc(c);
  if (doc) partes.push(doc);
  return partes.length ? partes.join(" · ") : null;
};

const formatDate = (d) => {
  try {
    return d ? format(parseISO(d), "dd/MM/yyyy HH:mm") : "-";
  } catch {
    return "-";
  }
};

const statusLabels = {
  open: "Em atendimento",
  pending: "Aguardando",
  closed: "Fechado",
};

const ACTION_LABELS = {
  "client.created": "Cliente cadastrado",
  "client.fields_updated": "Campos atualizados",
  "client.status_changed": "Status alterado",
  "client.source_changed": "Origem alterada",
  "client.tags_updated": "Tags atualizadas",
  "client.custom_fields_updated": "Campos personalizados atualizados",
};

const renderValue = (v) => {
  if (v === null || v === undefined || v === "") return '"(vazio)"';
  return `"${v}"`;
};

const ActivityItem = ({ activity }) => {
  const label = ACTION_LABELS[activity.action] || activity.action;
  const ch = activity.changes || {};
  const ts = activity.createdAt
    ? format(parseISO(activity.createdAt), "dd/MM/yyyy HH:mm")
    : "";

  let title = label;
  let lines = [];

  if (activity.action === "client.status_changed") {
    title = `Status alterado: ${ch.from?.name || "-"} → ${ch.to?.name || "-"}`;
  } else if (activity.action === "client.source_changed") {
    title = `Origem alterada: ${ch.from?.name || "-"} → ${ch.to?.name || "-"}`;
  } else if (activity.action === "client.tags_updated") {
    title = "Tags atualizadas";
    if (ch.added?.length)
      lines.push(`Adicionadas: ${ch.added.map((t) => t.name).join(", ")}`);
    if (ch.removed?.length)
      lines.push(`Removidas: ${ch.removed.map((t) => t.name).join(", ")}`);
  } else if (activity.action === "client.fields_updated") {
    title = "Campos atualizados";
    lines = (ch.fields || []).map(
      (f) => `${f.label}: ${renderValue(f.from)} → ${renderValue(f.to)}`
    );
  } else if (activity.action === "client.custom_fields_updated") {
    title = "Campos personalizados atualizados";
    lines = (ch.fields || []).map(
      (f) => `${f.name}: ${renderValue(f.from)} → ${renderValue(f.to)}`
    );
  }

  return (
    <Box
      style={{
        borderLeft: "3px solid #1976d2",
        paddingLeft: 12,
        marginBottom: 14,
      }}
    >
      <Typography variant="body2" style={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {lines.map((l, i) => (
        <Typography key={i} variant="body2" style={{ color: "#555" }}>
          {l}
        </Typography>
      ))}
      <Typography variant="caption" style={{ color: "#888" }}>
        {ts}
        {activity.user?.name ? ` · Por: ${activity.user.name}` : ""}
      </Typography>
    </Box>
  );
};

const ClientDetail = () => {
  const classes = useStyles();
  const { clientId } = useParams();
  const history = useHistory();

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  // null enquanto não carregou: evita piscar "Comercial (0)" antes da consulta.
  const [comercialCount, setComercialCount] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  const [tickets, setTickets] = useState([]);
  const [ticketsCount, setTicketsCount] = useState(0);
  const [ticketsHasMore, setTicketsHasMore] = useState(false);
  const [ticketsPage, setTicketsPage] = useState(1);
  const [ticketsStatus, setTicketsStatus] = useState("");
  const [loadingTickets, setLoadingTickets] = useState(false);

  const [activities, setActivities] = useState([]);
  const [activitiesCount, setActivitiesCount] = useState(0);
  const [activitiesHasMore, setActivitiesHasMore] = useState(false);
  const [activitiesPage, setActivitiesPage] = useState(1);
  const [loadingActivities, setLoadingActivities] = useState(false);

  const [workspace, setWorkspace] = useState(null);
  const [loadingWorkspace, setLoadingWorkspace] = useState(false);

  const [aiSetting, setAiSetting] = useState(null);
  const [aiSavingFor, setAiSavingFor] = useState(null);

  const fetchClient = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}`);
      setClient(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const fetchAiSetting = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/ai-settings`);
      setAiSetting(data);
    } catch (err) {
      // silencioso — pode nao ter permissao
    }
  }, [clientId]);

  const toggleAiField = async (field, value) => {
    setAiSavingFor(field);
    try {
      const { data } = await api.put(`/clients/${clientId}/ai-settings`, {
        [field]: value
      });
      setAiSetting(data);
      toast.success("Configuracao atualizada.");
    } catch (err) {
      toastError(err);
    } finally {
      setAiSavingFor(null);
    }
  };

  const fetchContacts = useCallback(async () => {
    setLoadingContacts(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/contacts`);
      setContacts(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingContacts(false);
    }
  }, [clientId]);

  const fetchTickets = useCallback(async () => {
    setLoadingTickets(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/tickets`, {
        params: {
          status: ticketsStatus || undefined,
          pageNumber: ticketsPage,
        },
      });
      if (ticketsPage === 1) setTickets(data.tickets);
      else setTickets((prev) => [...prev, ...data.tickets]);
      setTicketsCount(data.count);
      setTicketsHasMore(data.hasMore);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingTickets(false);
    }
  }, [clientId, ticketsPage, ticketsStatus]);

  const fetchActivities = useCallback(async () => {
    setLoadingActivities(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/activities`, {
        params: { pageNumber: activitiesPage },
      });
      if (activitiesPage === 1) setActivities(data.activities);
      else setActivities((prev) => [...prev, ...data.activities]);
      setActivitiesCount(data.count);
      setActivitiesHasMore(data.hasMore);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingActivities(false);
    }
  }, [clientId, activitiesPage]);

  const fetchWorkspace = useCallback(async () => {
    setLoadingWorkspace(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/workspace`);
      setWorkspace(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingWorkspace(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchClient();
    fetchAiSetting();
  }, [fetchClient, fetchAiSetting]);

  useEffect(() => {
    if (tab === 5) fetchWorkspace();
  }, [tab, fetchWorkspace]);

  useEffect(() => {
    if (tab === 0) fetchContacts();
  }, [tab, fetchContacts]);

  useEffect(() => {
    if (tab === 1) fetchTickets();
  }, [tab, fetchTickets]);

  useEffect(() => {
    if (tab === 2) fetchActivities();
  }, [tab, fetchActivities]);

  useEffect(() => {
    setTicketsPage(1);
  }, [ticketsStatus]);

  const handleSaved = (saved) => {
    setClient((prev) => ({ ...prev, ...saved }));
    fetchClient();
  };

  const openTicket = (ticketId) => history.push(`/tickets/${ticketId}`);

  if (loading || !client) {
    return (
      <MainContainer>
        <PageHeader title="Carregando…" onBack={() => history.push("/clients")} backLabel="Voltar para Clientes" />
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <ClientModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        clientId={client.id}
        onSave={handleSaved}
      />

      <PageHeader
        title={displayName(client)}
        subtitle={clientSubtitle(client)}
        onBack={() => history.push("/clients")}
        backLabel="Voltar para Clientes"
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<EditIcon />}
            onClick={() => setEditOpen(true)}
          >
            Editar
          </Button>
        }
      />

      <Box className={classes.scroll}>
        <Paper className={classes.paper} variant="outlined">
          <Box display="flex" alignItems="center" gridGap={12} flexWrap="wrap">
            <MemoryOutlinedIcon fontSize="small" style={{ color: "#6a1b9a" }} />
            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
              Atendimento por IA
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={aiSetting ? aiSetting.botEnabled !== false : true}
                  disabled={aiSavingFor === "botEnabled" || !aiSetting}
                  onChange={e => {
                    const checked = e.target.checked;
                    toggleAiField("botEnabled", checked);
                  }}
                  color="primary"
                />
              }
              label="Bot/IA ativo para este cliente"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={aiSetting ? !!aiSetting.humanPriority : false}
                  disabled={aiSavingFor === "humanPriority" || !aiSetting}
                  onChange={e => {
                    const checked = e.target.checked;
                    toggleAiField("humanPriority", checked);
                  }}
                  color="primary"
                />
              }
              label="Prioridade humana (bloqueia o bot mesmo se ativo)"
            />
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ flexBasis: "100%", marginTop: 4 }}
            >
              O bot so executa quando ambos os toggles do cliente E o
              "Atendimento por bot/IA habilitado" da fila estiverem ativos.
            </Typography>
          </Box>
        </Paper>
        <Paper className={classes.paper} variant="outlined">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <div className={classes.label}>Tipo</div>
              <div className={classes.value}>
                {client.personType === "PJ" ? "Pessoa Jurídica" : "Pessoa Física"}
              </div>
            </Grid>
            <Grid item xs={12} sm={4}>
              <div className={classes.label}>CPF/CNPJ</div>
              <div className={classes.value}>{displayDoc(client) || "-"}</div>
            </Grid>
            <Grid item xs={12} sm={4}>
              <div className={classes.label}>Status</div>
              <div className={classes.chipRow}>
                {client.clientStatus ? (
                  <Chip
                    size="small"
                    label={client.clientStatus.name}
                    className={classes.statusChip}
                    style={{ backgroundColor: client.clientStatus.color }}
                  />
                ) : (
                  <Chip size="small" label={client.status || "-"} />
                )}
              </div>
            </Grid>
            <Grid item xs={12} sm={4}>
              <div className={classes.label}>E-mail</div>
              <div className={classes.value}>{client.email || "-"}</div>
            </Grid>
            <Grid item xs={12} sm={4}>
              <div className={classes.label}>Telefone</div>
              <div className={classes.value}>
                {client.mobilePhone || client.phone || "-"}
              </div>
            </Grid>
            <Grid item xs={12} sm={4}>
              <div className={classes.label}>Origem</div>
              <div className={classes.chipRow}>
                {client.source ? (
                  <Chip
                    size="small"
                    label={client.source.name}
                    style={{ backgroundColor: client.source.color, color: "#fff" }}
                  />
                ) : (
                  "-"
                )}
              </div>
            </Grid>
            {client.personType === "PJ" && (
              <>
                <Grid item xs={12} sm={6}>
                  <div className={classes.label}>Razão Social</div>
                  <div className={classes.value}>{client.companyName || "-"}</div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className={classes.label}>Nome Fantasia</div>
                  <div className={classes.value}>{client.tradeName || "-"}</div>
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <div className={classes.label}>Tags</div>
              <div className={classes.chipRow}>
                {(client.tags || []).length === 0 && "-"}
                {(client.tags || []).map((tag) => (
                  <Chip
                    key={tag.id}
                    size="small"
                    label={tag.name}
                    style={{ backgroundColor: tag.color, color: "#fff" }}
                  />
                ))}
              </div>
            </Grid>
            {(client.customFieldValues || []).length > 0 && (
              <Grid item xs={12}>
                {/* Estes tem catalogo administrado (ClientFieldDefinition) —
                    mesmo nome e mesmo tipo em todo cliente. Por isso mostra
                    todos, mesmo os vazios: um campo sem resposta ainda
                    conta informacao ("ninguem preencheu"), diferente do
                    extraInfo abaixo, que e criado sob demanda. */}
                <div className={classes.label}>Campos personalizados</div>
                <Grid container spacing={2} style={{ marginTop: 4 }}>
                  {client.customFieldValues
                    .filter((v) => v.definition && v.definition.active)
                    .map((v) => (
                      <Grid item xs={12} sm={6} md={4} key={v.id}>
                        <div className={classes.label}>{v.definition.label}</div>
                        <div className={classes.value}>
                          {v.definition.type === "boolean"
                            ? v.value === "true" ? "Sim" : v.value === "false" ? "Não" : "-"
                            : v.value && v.value !== "" ? v.value : "-"}
                        </div>
                      </Grid>
                    ))}
                </Grid>
              </Grid>
            )}
            {(client.extraInfo || []).length > 0 && (
              <Grid item xs={12}>
                <div className={classes.label}>Informações adicionais</div>
                <Grid container spacing={2} style={{ marginTop: 4 }}>
                  {client.extraInfo.map((info) => (
                    <Grid item xs={12} sm={6} md={4} key={info.id || info.name}>
                      <div className={classes.label}>{info.name}</div>
                      <div className={classes.value}>
                        {info.value && info.value !== "" ? info.value : "-"}
                      </div>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
            {client.notes && (
              <Grid item xs={12}>
                <div className={classes.label}>Observações</div>
                <div className={classes.value} style={{ whiteSpace: "pre-wrap" }}>
                  {client.notes}
                </div>
              </Grid>
            )}
            {(client.addresses || []).length > 0 && (
              <Grid item xs={12}>
                <div className={classes.label}>Endereços</div>
                {client.addresses.map((a) => (
                  <div key={a.id} className={classes.addressBox}>
                    <strong>{a.label || "Endereço"}</strong>
                    {a.isPrimary ? " (principal)" : ""}
                    <div>
                      {[a.street, a.number].filter(Boolean).join(", ")}
                      {a.complement ? ` - ${a.complement}` : ""}
                    </div>
                    <div>
                      {[a.neighborhood, a.city, a.state].filter(Boolean).join(" - ")}
                      {a.zipCode ? ` - CEP ${a.zipCode}` : ""}
                    </div>
                    {a.country && <div>{a.country}</div>}
                  </div>
                ))}
              </Grid>
            )}
          </Grid>
        </Paper>

        <Paper variant="outlined">
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label={`Números vinculados (${contacts.length})`} />
            <Tab label={`Atendimentos (${ticketsCount})`} />
            <Tab label={`Histórico (${activitiesCount})`} />
            <Tab
              label={
                comercialCount === null
                  ? "Comercial"
                  : `Comercial (${comercialCount})`
              }
            />
            <Tab label="Datas" />
            <Tab label="Workspace" />
          </Tabs>

          {tab === 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Nome</TableCell>
                  <TableCell>Número</TableCell>
                  <TableCell>E-mail</TableCell>
                  <TableCell align="center">Tipo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell style={{ width: 60 }}>
                      <Avatar src={withMediaToken(c.profilePicUrl)} />
                    </TableCell>
                    <TableCell>{c.name}</TableCell>
                    <TableCell>{c.number}</TableCell>
                    <TableCell>{c.email || "-"}</TableCell>
                    <TableCell align="center">
                      {c.isGroup ? "Grupo" : "Individual"}
                    </TableCell>
                  </TableRow>
                ))}
                {contacts.length === 0 && !loadingContacts && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Nenhum número vinculado.
                    </TableCell>
                  </TableRow>
                )}
                {loadingContacts && <TableRowSkeleton avatar columns={4} />}
              </TableBody>
            </Table>
          )}

          {tab === 1 && (
            <>
              <Box display="flex" alignItems="center" p={1} gridGap={8}>
                <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={ticketsStatus}
                    onChange={(e) => setTicketsStatus(e.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="open">Em atendimento</MenuItem>
                    <MenuItem value="pending">Aguardando</MenuItem>
                    <MenuItem value="closed">Fechado</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Contato</TableCell>
                    <TableCell>Atendente</TableCell>
                    <TableCell>Fila</TableCell>
                    <TableCell>Conexão</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell>Atualizado</TableCell>
                    <TableCell align="center" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tickets.map((t) => (
                    <TableRow key={t.id} hover>
                      <TableCell>{t.id}</TableCell>
                      <TableCell>
                        {t.contact?.name} <br />
                        <span style={{ fontSize: 11, color: "#777" }}>
                          {t.contact?.number}
                        </span>
                      </TableCell>
                      <TableCell>{t.user?.name || "-"}</TableCell>
                      <TableCell>
                        {t.queue ? (
                          <Chip
                            size="small"
                            label={t.queue.name}
                            style={{ backgroundColor: t.queue.color, color: "#fff" }}
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>{t.whatsapp?.name || "-"}</TableCell>
                      <TableCell align="center">
                        {statusLabels[t.status] || t.status}
                      </TableCell>
                      <TableCell>{formatDate(t.updatedAt)}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => openTicket(t.id)}>
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {tickets.length === 0 && !loadingTickets && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        Nenhum atendimento encontrado.
                      </TableCell>
                    </TableRow>
                  )}
                  {loadingTickets && <TableRowSkeleton columns={7} />}
                </TableBody>
              </Table>
              {ticketsHasMore && !loadingTickets && (
                <Box display="flex" justifyContent="center" p={1}>
                  <Button onClick={() => setTicketsPage((p) => p + 1)}>
                    Carregar mais
                  </Button>
                </Box>
              )}
            </>
          )}

          {tab === 2 && (
            <Box p={2}>
              {activities.length === 0 && !loadingActivities && (
                <Typography variant="body2" color="textSecondary" align="center">
                  Nenhuma atividade registrada.
                </Typography>
              )}
              {activities.map((a) => (
                <ActivityItem key={a.id} activity={a} />
              ))}
              {loadingActivities && (
                <Typography variant="body2" color="textSecondary" align="center">
                  Carregando...
                </Typography>
              )}
              {activitiesHasMore && !loadingActivities && (
                <Box display="flex" justifyContent="center" pt={1}>
                  <Button onClick={() => setActivitiesPage((p) => p + 1)}>
                    Carregar mais
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {tab === 3 && (
            <ClientCommercial
              clientId={client.id}
              onCounts={setComercialCount}
            />
          )}
          {tab === 4 && <ClientKeyDates clientId={client.id} />}

          {tab === 5 && (
            <Box p={2}>
              {loadingWorkspace && (
                <Typography variant="body2" color="textSecondary" align="center">
                  Carregando...
                </Typography>
              )}
              {!loadingWorkspace && workspace && (
                <>
                  <Grid container spacing={1} style={{ marginBottom: 12 }}>
                    {[
                      { label: "Projetos", value: workspace.indicators?.projectsCount ?? 0 },
                      { label: "Tarefas totais", value: workspace.indicators?.totalTasks ?? 0 },
                      { label: "Abertas", value: workspace.indicators?.openTasks ?? 0 },
                      { label: "Concluídas", value: workspace.indicators?.doneTasks ?? 0 },
                      { label: "Atrasadas", value: workspace.indicators?.overdueTasks ?? 0 },
                      { label: "Conclusão", value: `${workspace.indicators?.completionRate ?? 0}%` },
                      { label: "Próx. compromissos", value: workspace.indicators?.upcomingAppointments ?? 0 },
                    ].map((k) => (
                      <Grid item xs={6} sm={3} md={2} key={k.label}>
                        <Paper variant="outlined" style={{ padding: 8, textAlign: "center" }}>
                          <Typography variant="caption" color="textSecondary">{k.label}</Typography>
                          <Typography variant="h6">{k.value}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  <Typography variant="subtitle1" style={{ marginTop: 8 }}>Projetos</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nome</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Prazo</TableCell>
                        <TableCell align="right">Tarefas</TableCell>
                        <TableCell align="right">Conclusão</TableCell>
                        <TableCell align="center" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(workspace.projects || []).map((p) => (
                        <TableRow key={p.id} hover>
                          <TableCell>{p.name}</TableCell>
                          <TableCell>{p.status}</TableCell>
                          <TableCell>{p.dueDate || "-"}</TableCell>
                          <TableCell align="right">{p.stats?.total ?? 0}</TableCell>
                          <TableCell align="right">{p.stats?.completionRate ?? 0}%</TableCell>
                          <TableCell align="center">
                            <IconButton size="small" onClick={() => history.push(`/projects/${p.id}`)}>
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      {(workspace.projects || []).length === 0 && (
                        <TableRow><TableCell colSpan={6} align="center">Nenhum projeto.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>

                  <Typography variant="subtitle1" style={{ marginTop: 16 }}>Tarefas</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Título</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Prio.</TableCell>
                        <TableCell>Projeto</TableCell>
                        <TableCell>Responsável</TableCell>
                        <TableCell>Prazo</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(workspace.tasks || []).map((t) => (
                        <TableRow key={t.id} hover>
                          <TableCell>{t.id}</TableCell>
                          <TableCell>{t.title}</TableCell>
                          <TableCell>{t.status}</TableCell>
                          <TableCell>{t.priority}</TableCell>
                          <TableCell>{t.project?.name || "—"}</TableCell>
                          <TableCell>{t.assignee?.name || "-"}</TableCell>
                          <TableCell>{t.dueDate || "-"}</TableCell>
                        </TableRow>
                      ))}
                      {(workspace.tasks || []).length === 0 && (
                        <TableRow><TableCell colSpan={7} align="center">Nenhuma tarefa.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>

                  <Typography variant="subtitle1" style={{ marginTop: 16 }}>Próximos compromissos (60 dias)</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Título</TableCell>
                        <TableCell>Início</TableCell>
                        <TableCell>Fim</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(workspace.appointments || []).map((a) => (
                        <TableRow key={a.id} hover>
                          <TableCell>{a.title}</TableCell>
                          <TableCell>{formatDate(a.startDate)}</TableCell>
                          <TableCell>{formatDate(a.endDate)}</TableCell>
                          <TableCell>{a.status}</TableCell>
                        </TableRow>
                      ))}
                      {(workspace.appointments || []).length === 0 && (
                        <TableRow><TableCell colSpan={4} align="center">Nenhum compromisso.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </>
              )}
            </Box>
          )}
        </Paper>
      </Box>
    </MainContainer>
  );
};

export default ClientDetail;
